import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { SettingsService } from './settings.service';
import { BaseService } from './base.service';
import { CaseSummary } from '../../shared/models/caseSummary';
import { CaseSummaryAdapter } from '@secca/shared/models/caseSummary';
import { CaseSummaryFilter } from '../../shared/models/caseSummaryFilter';
import { UserDto, UserDtoAdapter } from '../../shared/models/userDto';
import { CaseHistoryEntry, CaseHistoryEntryAdapter } from '@secca/shared/models/caseHistoryEntry';

@Injectable({
  providedIn: 'root',
})
export class CaseSummaryService extends BaseService {
  map: any = '';
  private readonly caseSummary = new BehaviorSubject<CaseSummary>(undefined);
  private readonly caseHistory = new BehaviorSubject<CaseSummary>(undefined);

  constructor(
    private http: HttpClient,
    private settingsService: SettingsService,
    private userDtoAdapter: UserDtoAdapter,
    private caseHistoryDtoAdapter: CaseHistoryEntryAdapter,
    private caseSummaryAdapter: CaseSummaryAdapter
  ) {
    super(settingsService);
  }

  private caseSummaryMap = new Map<number, BehaviorSubject<CaseSummary>>();
  private caseHistoryMap = new Map<number, BehaviorSubject<CaseSummary>>();


  private initCaseSummaryMapBehaviorSubject(caseId: string) {
    const id = +caseId;
    if (!this.caseSummaryMap.has(id)) {
      this.caseSummaryMap.set(id, new BehaviorSubject<CaseSummary>(null));
    }
  }

  public getCaseSummarySubject(caseId: string): Observable<CaseSummary> {
    this.initCaseSummaryMapBehaviorSubject(caseId);
    return this.caseSummaryMap.get(+caseId).asObservable();
  }

  public setCaseSummarySubject(caseId: string, caseSummary: CaseSummary) {
    this.initCaseSummaryMapBehaviorSubject(caseId);
    this.caseSummaryMap.get(+caseId).next(caseSummary);
  }

  public deleteCaseSummaryAndHistorySubject(caseId: string) {
    const id = +caseId;
    const subjectSummery = this.caseSummaryMap.get(id);
    const subjectHistory = this.caseHistoryMap.get(id);

    if (subjectSummery) {
      subjectSummery.complete();
      this.caseSummaryMap.delete(id);
    }
    if (subjectHistory) {
      subjectHistory.complete();
      this.caseHistoryMap.delete(id);
    }
  }

  public getCaseHistorySubject(caseId: string): Observable<CaseSummary> {
    this.initCaseHistoryMapBehaviorSubject(caseId);
    return this.caseHistoryMap.get(+caseId).asObservable();
  }

  public setCaseHistorySubject(caseId: string, caseSummary: CaseSummary) {
    this.initCaseHistoryMapBehaviorSubject(caseId);
    this.caseHistoryMap.get(+caseId).next(caseSummary);
  }

  private initCaseHistoryMapBehaviorSubject(caseId: string) {
    const id = +caseId;
    if (!this.caseHistoryMap.has(id)) {
      this.caseHistoryMap.set(id, new BehaviorSubject<CaseSummary>(null));
    }
  }

  public getCaseHistory(caseId: string, filter: CaseSummaryFilter): Observable<CaseSummary> {
    return this.http
      .get<CaseSummary>(this.baseURL + `casehistory/history-decorated/` + caseId, {
        params: filter.generateHttpParamsForHistory(),
        headers: this.jsonHeaders,
      })
      .pipe(map(item => this.caseSummaryAdapter.adapt(item)));
    }

  public getCaseSummary(caseId: string, filter: CaseSummaryFilter): Observable<CaseSummary> {
    return this.http
      .get<CaseSummary>(this.baseURL + `casehistory/summary-decorated/` + caseId, {
        params: filter.generateHttpParamsForSummary(),
        headers: this.jsonHeaders,
      })
      .pipe(map(item => this.caseSummaryAdapter.adapt(item)));
    }

  public getCaseSummaryAuthors(caseId: string): Observable<UserDto[]> {
    return this.http
      .get<UserDto[]>(this.baseURL + `bff/casehistory/dictionary/authors/` + caseId, { headers: this.jsonHeaders })
      .pipe(map((data: any[]) => data.map(item => this.userDtoAdapter.adapt(item))));
  }

  public getLastActionInCaseSummary(caseId: string): Observable<CaseHistoryEntry> {
    return this.http
      .get<CaseHistoryEntry>(this.baseURL + 'casehistory/summary/last-action/' + caseId)
      .pipe(map(item => this.caseHistoryDtoAdapter.adapt(item)));
  }
}
